import type { TrackedBatch } from "../batchGeneration/types";

export type ProcessingJobKind = "product" | "work_product";

export type ProcessingJobStatus =
    | "queued"
    | "pending"
    | "started"
    | "running"
    | "processing"
    | "completed"
    | "succeeded"
    | "failed"
    | "error"
    | "cancelled"
    | string;

export interface ProcessingJobActivity {
    id: string;
    type: string;
    label: string;
    status: ProcessingJobStatus;
    progress: number | null;
    message: string | null;
    externalJobId: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface ProcessingJob {
    id: string;
    kind: ProcessingJobKind;
    status: ProcessingJobStatus;
    progress: number | null;
    message: string | null;
    productId: string | null;
    productName: string | null;
    workProductId: string | null;
    workProductName: string | null;
    currentActivityId: string | null;
    activities: ProcessingJobActivity[];
    createdAt: string | null;
    startedAt: string | null;
    updatedAt: string | null;
    completedAt: string | null;
}

export interface ProcessingJobsResponse {
    jobs: ProcessingJob[];
}

const ACTIVE_STATUSES = new Set([
    "not_started",
    "queued",
    "pending",
    "accepted",
    "waiting",
    "recovering",
    "started",
    "running",
    "processing",
    "section_completed",
    "in_progress"
]);

const RUNNING_STATUSES = new Set(["recovering", "started", "running", "processing", "section_completed", "in_progress"]);

const QUEUED_STATUSES = new Set(["not_started", "queued", "pending", "accepted", "waiting"]);

const SUCCESS_STATUSES = new Set(["complete", "completed", "success", "succeeded"]);
const FAILED_STATUSES = new Set(["canceled", "cancelled", "error", "failed"]);

export const normalizeProcessingJobStatus = (status: unknown): string =>
    String(status || "pending")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");

export const isActiveProcessingJob = (job: Pick<ProcessingJob, "status">): boolean => ACTIVE_STATUSES.has(normalizeProcessingJobStatus(job.status));

export const isRunningProcessingJob = (job: Pick<ProcessingJob, "status">): boolean => RUNNING_STATUSES.has(normalizeProcessingJobStatus(job.status));

export const isQueuedProcessingJob = (job: Pick<ProcessingJob, "status">): boolean => QUEUED_STATUSES.has(normalizeProcessingJobStatus(job.status));

export const isSuccessfulProcessingJob = (job: Pick<ProcessingJob, "status">): boolean => SUCCESS_STATUSES.has(normalizeProcessingJobStatus(job.status));

export const isFailedProcessingJob = (job: Pick<ProcessingJob, "status">): boolean => FAILED_STATUSES.has(normalizeProcessingJobStatus(job.status));

export const isFinishedProcessingJob = (job: Pick<ProcessingJob, "status">): boolean => isSuccessfulProcessingJob(job) || isFailedProcessingJob(job);

export interface BackgroundActivityContextValue {
    jobs: ProcessingJob[];
    batches: TrackedBatch[];
    activeCount: number;
    runningCount: number;
    queuedCount: number;
    finishedCount: number;
    failedCount: number;
    isLoading: boolean;
    isRefreshing: boolean;
    isClearing: boolean;
    retryingJobId: string | null;
    retryErrors: Record<string, string>;
    error: string | null;
    isOpen: boolean;
    focusedJobId: string | null;
    toggle: () => void;
    close: () => void;
    openForJob: (jobId?: string | null) => void;
    refresh: () => Promise<void>;
    clearFinished: () => Promise<void>;
    retryFailedJob: (jobId: string) => Promise<void>;
}
